import { GetSectionIconUrl } from '@assets/component/SectionIcons/SectionIcon';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { ScoreUtils, useScoreStore } from '@assets/model/ScoreUtils';
import { useStore } from '@assets/model/Store';
import { observer } from 'mobx-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import * as style from './ChartStyles.module.less';
import { ChartUtils } from './ChartShared';
import classNames from 'classnames';

interface IChartScoreBreakdownProps {
    reportId: string;
    hideUnderline?: boolean;
}

export const ChartScoreBreakdown = observer((p: IChartScoreBreakdownProps) => {
    const store = useStore();
    const q = store.createDataAccess(p.reportId);

    const hideUnderline = p.hideUnderline === true;
    if (q == null) return null;

    var scoreUtils = new ScoreUtils(q);
    var scoreStore = useScoreStore();
    var groups = scoreStore.SCORE_FIELDS_GROUPS;
    var data = [];

    const GEN_SCORE_ID = 'ALL';
    const scoreTotal = scoreUtils.calcScorePercentile();
    data.push({
        sectionId: GEN_SCORE_ID,
        name: 'Overall Score', //Hack so split ' ' works
        Score: scoreTotal ? (scoreTotal * 100).toFixed(0) : 'N/A'
    });

    groups.forEach(group => {
        if (scoreUtils.is_section_used_for_scoring_by_id(group.sectionId)) {
            data.push({
                sectionId: group.sectionId,
                name: ScoreUtils.get_section_name(group.sectionId),
                Score: (
                    scoreUtils.calcSectionPercentileById(group.sectionId) * 100
                ).toFixed(0)
            });
        }
    });

    const renderCustomAxisTick = ({ x, y, payload }) => {
        const sectionId =
            payload.value == GEN_SCORE_ID ? 'overview' : payload.value;

        const icon = GetSectionIconUrl(sectionId);

        const sectionName = data
            .find(section => section.sectionId === payload.value)
            .name.split(' ')[0];
        return (
            <g transform={`translate(${x},${y})`}>
                <image
                    href={icon} // ✅ Replace with your actual SVG URL
                    x={-30}
                    y={-12}
                    width={20}
                    height={20}
                />
                {/* Text Label */}
                <text
                    fontSize="12" // ✅ Set readable font size
                    fontFamily="LustTextMedium" // ✅ Keep it clean
                    x={-10}
                    y={-25}
                    textAnchor="end"
                    fill="#212529"
                >
                    {sectionName}
                </text>
            </g>
        );
    };

    const titleCls = classNames(style.title, {
        [style.titleUnderline]: !hideUnderline
    });

    return (
        <Flex direction="column" fillCell={true}>
            <div className={titleCls}>Score Summary</div>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    layout="vertical"
                    width={500}
                    height={300}
                    data={data}
                    margin={{
                        top: 20,
                        left: 20,
                        right: 20,
                        bottom: 20
                    }}
                    barGap={5}
                    barCategoryGap={10}
                >
                    <defs>{ChartUtils.getBarGradient()}</defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        type="number"
                        domain={[0, 'dataMax']}
                        allowDataOverflow={true}
                        ticks={[0, 25, 50, 75, 100]}
                    />
                    <YAxis
                        dataKey="sectionId"
                        type="category"
                        tick={renderCustomAxisTick}
                    />

                    <Tooltip
                        labelFormatter={value => {
                            return data.find(
                                section => section.sectionId === value
                            ).name;
                        }}
                        formatter={(value, name, props) => `${value}%`}
                    />

                    <Bar
                        dataKey="Score"
                        radius={[0, 10, 10, 0]}
                        maxBarSize={100}
                    >
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={
                                    entry.sectionId === GEN_SCORE_ID
                                        ? 'url(#barGradientGold)'
                                        : 'url(#barGradientBlue)'
                                }
                                style={{
                                    filter: ChartUtils.SHADOW_FILTER
                                }}
                            />
                        ))}
                        <LabelList
                            dataKey="Score"
                            position="insideRight"
                            formatter={v => v + '%'}
                            fill="white"
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Flex>
    );
});
